import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Collapse,
  Button,
  Progress,
  Table,
  Spinner,
} from "reactstrap";
import {
  FaMoneyBillWave,
  FaChartPie,
  FaBalanceScale,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { getFinanceDashboardData } from "helpers/backend_helper";

// ─── Design Tokens (strictly matching existing SCSS / Skote theme) ─────────────
const BRAND_PRIMARY = "#da7938";
const TEXT_DARK = "#2a3042";
const TEXT_MUTED = "#74788d";
const BORDER_COLOR = "#eff2f7";
const COLOR_SUCCESS = "#34c38f";
const COLOR_DANGER = "#f46a6a";
const COLOR_INFO = "#50a5f1";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  `₹${Number(Math.round(Number(amount || 0))).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}`;

const calculatePercentage = (paid, total) => {
  const p = Number(paid || 0);
  const t = Number(total || 0);
  return t === 0 ? 0 : ((p / t) * 100).toFixed(1);
};

// ─── Main Academic Fees Dashboard ─────────────────────────────────────────────
const AcademicFeesDashboard = ({ aYear }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedCenter, setExpandedCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(aYear);

  useEffect(() => {
    if (!selectedYear) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getFinanceDashboardData({ academic_year: selectedYear });
        if (response?.data.success) {
          setDashboardData(response.data.data);
          const firstCenter = response.data.data.centers?.[0];
          if (firstCenter) setExpandedCenter(firstCenter.center_id);
        } else {
          console.error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: 320 }}>
        <Spinner color="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
        <p className="text-muted mt-3 mb-0 fs-6">Loading fees dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center text-danger py-5 fw-semibold">
        Failed to load dashboard data. Please try again or refresh the page.
      </div>
    );
  }

  const { overall, centers } = dashboardData;

  // ── Exact Business Logic Calculations ──────────────────────────────────────
  // term_total_amount from the backend already excludes skipped installments,
  // so term_skipped_amount is ADDED here to reflect the full billed amount.
  const expectedTermFee = Number(overall.term_total_amount);
  const skippedTermFee = Number(overall.term_skipped_amount || 0);

  const totalCollected =
    Number(overall.term_paid_amount) +
    Number(overall.term_historical_paid_amount) +
    Number(overall.otherfees_paid_amount) +
    Number(overall.observation_paid_amount) +
    Number(overall.registeration_paid_amount) +
    Number(overall.term_latefee_paid_amount) +
    Number(overall.otherfees_latefee_paid_amount);

  const totalAmount =
    expectedTermFee +
    skippedTermFee +
    Number(overall.term_expected_latefee_amount) +
    Number(overall.otherfees_total_amount) +
    Number(overall.observation_paid_amount) +
    Number(overall.registeration_paid_amount) +
    Number(overall.term_historical_paid_amount) +
    Number(overall.term_historical_latefee_paid_amount || 0) -
    Number(overall.term_expected_discount);

  const totalBalance = Math.max(0, totalAmount - totalCollected);

  const totalLateFee =
    Number(overall.term_latefee_paid_amount) + Number(overall.otherfees_latefee_paid_amount);

  const totalPaymentChannelAmount = Object.values(overall.payment_channels || {}).reduce(
    (sum, val) => sum + Number(val),
    0
  );

  const otherfeesExpectedLatefeeAmount =
    overall.otherfees_expected_latefee_amount !== undefined
      ? Number(overall.otherfees_expected_latefee_amount)
      : (centers || []).reduce((sum, c) => sum + Number(c.otherfees_expected_latefee_amount || 0), 0);

  const termLateFeeBalance = Math.max(
    0,
    Number(overall.term_expected_latefee_amount || 0) - Number(overall.term_latefee_paid_amount || 0)
  );
  const otherLateFeeBalance = Math.max(
    0,
    Number(otherfeesExpectedLatefeeAmount) - Number(overall.otherfees_latefee_paid_amount || 0)
  );
  const totalLateFeeBalance = termLateFeeBalance + otherLateFeeBalance;
  const termBaseBalance = Math.max(0, Number(overall.term_balance_amount || 0) - termLateFeeBalance);
  const otherBaseBalance = Math.max(0, Number(overall.otherfees_balance_amount || 0) - otherLateFeeBalance);

  const overallPercent = calculatePercentage(totalCollected, totalAmount);

  // ── Tooltip Breakdowns ─────────────────────────────────────────────────────
  const collectedTooltipRows = [
    {
      label: "Term Fees Paid",
      value: formatCurrency(
        Number(overall.term_paid_amount) + Number(overall.term_historical_paid_amount)
      ),
    },
    { label: "Other Fees Paid", value: formatCurrency(overall.otherfees_paid_amount) },
    { label: "Observation Fees", value: formatCurrency(overall.observation_paid_amount) },
    { label: "Registration Fees", value: formatCurrency(overall.registeration_paid_amount) },
    {
      label: "Late Fees Paid",
      value: formatCurrency(
        Number(overall.term_latefee_paid_amount) + Number(overall.otherfees_latefee_paid_amount)
      ),
    },
  ];

  const billedTooltipRows = [
    { label: "Term Fees (Billed)", value: formatCurrency(expectedTermFee) },
    ...(skippedTermFee > 0
      ? [
          {
            label: "Skipped Amount (Included)",
            value: `+${formatCurrency(skippedTermFee)}`,
            color: COLOR_INFO,
          },
        ]
      : []),
    {
      label: "Other Fees (Billed)",
      value: formatCurrency(
        Number(overall.otherfees_total_amount) - Number(otherfeesExpectedLatefeeAmount)
      ),
    },
    {
      label: "Expected Late Fee",
      value: formatCurrency(
        Number(overall.term_expected_latefee_amount) + Number(otherfeesExpectedLatefeeAmount)
      ),
    },
    { label: "Historical Paid", value: formatCurrency(overall.term_historical_paid_amount) },
    { label: "Historical Late Fee Paid", value: formatCurrency(overall.term_historical_latefee_paid_amount || 0) },
    { label: "Observation Fees", value: formatCurrency(overall.observation_paid_amount) },
    { label: "Registration Fees", value: formatCurrency(overall.registeration_paid_amount) },
    {
      label: "Expected Discount",
      value: `-${formatCurrency(overall.term_expected_discount)}`,
      color: COLOR_DANGER,
    },
  ];

  const balanceTooltipRows = [
    { label: "Term Fees Balance", value: formatCurrency(termBaseBalance) },
    { label: "Other Fees Balance", value: formatCurrency(otherBaseBalance) },
    {
      label: "Late Fee Expected",
      value: formatCurrency(
        Number(overall.term_expected_latefee_amount) + Number(otherfeesExpectedLatefeeAmount)
      ),
    },
    { label: "Late Fee Paid", value: formatCurrency(totalLateFee) },
    { label: "Late Fee Unpaid", value: formatCurrency(totalLateFeeBalance) },
  ];

  return (
    <Container fluid className="py-3 px-2 px-md-3">

      {/* ── 1. Top KPI Summary Metrics ─────────────────────────────────────── */}
      <Row className="g-3 mb-3">
        <Col xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Collected"
            value={formatCurrency(totalCollected)}
            icon={<FaMoneyBillWave color={COLOR_SUCCESS} className="me-2" />}
            topAccentColor={COLOR_SUCCESS}
            subText={`${overallPercent}% of total billed`}
            tooltipTitle="Collected Breakdown"
            tooltipRows={collectedTooltipRows}
            totalLabel="Total Collected"
            totalValue={formatCurrency(totalCollected)}
          />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Billed"
            value={formatCurrency(totalAmount)}
            icon={<FaChartPie color={BRAND_PRIMARY} className="me-2" />}
            topAccentColor={BRAND_PRIMARY}
            tooltipTitle="Total Billed Breakdown"
            tooltipRows={billedTooltipRows}
            totalLabel="Total Billed"
            totalValue={formatCurrency(totalAmount)}
          />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <MetricCard
            title="Balance Due"
            value={formatCurrency(totalBalance)}
            valueColor={COLOR_DANGER}
            icon={<FaBalanceScale color={COLOR_DANGER} className="me-2" />}
            topAccentColor={COLOR_DANGER}
            tooltipTitle="Balance Due Breakdown"
            tooltipRows={balanceTooltipRows}
            totalLabel="Total Balance Due"
            totalValue={formatCurrency(totalBalance)}
          />
        </Col>
      </Row>

      {/* Overall Progress Tracker */}
      <Card className="border shadow-none mb-3">
        <CardBody className="py-2 px-3">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <span className="fw-semibold text-secondary" style={{ fontSize: "0.8rem" }}>
              Overall Collection Progress
            </span>
            <span className="fw-bold text-success" style={{ fontSize: "0.85rem" }}>
              {overallPercent}%
            </span>
          </div>
          <Progress
            value={overallPercent}
            color="success"
            style={{ height: "6px", borderRadius: "3px" }}
          />
        </CardBody>
      </Card>

      {/* ── 2. Overall Payment Channels ────────────────────────────────────── */}
      <Card className="border shadow-none mb-4">
        <CardBody className="py-3 px-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-bold" style={{ fontSize: "0.85rem", color: TEXT_DARK }}>
              Overall Payment Channels
            </span>
            <span className="text-muted" style={{ fontSize: "0.78rem" }}>
              Total: <strong style={{ color: BRAND_PRIMARY }}>{formatCurrency(totalPaymentChannelAmount)}</strong>
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(overall.payment_channels || {}).map(([channel, amount], i) => (
              <div
                key={i}
                className="bg-light border rounded px-3 py-2 flex-fill"
                style={{ minWidth: "120px", maxWidth: "200px" }}
              >
                <div className="text-muted fw-semibold text-uppercase" style={{ fontSize: "0.68rem" }}>
                  {channel}
                </div>
                <div className="fw-bold mt-1" style={{ fontSize: "0.92rem", color: TEXT_DARK }}>
                  {formatCurrency(amount)}
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* ── 3. Center-wise Fee Collection Section Header ────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 px-1">
        <h5 className="fw-bold mb-0" style={{ fontSize: "1rem", color: TEXT_DARK }}>
          Center-wise Fee Collection
        </h5>
        <span className="text-muted" style={{ fontSize: "0.8rem" }}>
          {centers.length} {centers.length === 1 ? "Center" : "Centers"} Total
        </span>
      </div>

      {/* ── 4. Center Cards List ───────────────────────────────────────────── */}
      {centers.map((center) => {
        const collected =
          Number(center.term_paid_amount) +
          Number(center.otherfees_paid_amount) +
          Number(center.observation_paid_amount) +
          Number(center.registeration_paid_amount) +
          Number(center.term_latefee_paid_amount || 0) +
          Number(center.otherfees_latefee_paid_amount || 0);

        const totalAmt =
          Number(center.term_total_amount) +
          Number(center.term_expected_latefee_amount) +
          Number(center.otherfees_total_amount) -
          Number(center.term_refund_amount) -
          Number(center.otherfees_refund_amount);

        const collectionPercent = calculatePercentage(collected, totalAmt);
        const isExpanded = expandedCenter === center.center_id;

        const totalChannel = Object.values(center.payment_channels || {}).reduce(
          (sum, val) => sum + Number(val),
          0
        );

        return (
          <Card key={center.center_id} className="border shadow-none mb-3" style={{ overflow: "hidden" }}>
            {/* Center Summary Banner */}
            <div className="p-3 bg-white border-bottom">
              <Row className="align-items-center g-3">
                {/* Center Title & Location */}
                <Col xs={12} md={4}>
                  <div className="fw-bold text-uppercase" style={{ fontSize: "0.92rem", color: BRAND_PRIMARY, letterSpacing: "0.3px" }}>
                    {center.center_name || "Unknown Center"}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    📍 {center.center_location}
                  </div>
                </Col>

                {/* Key Amounts Summary */}
                <Col xs={12} md={5}>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div>
                      <div className="text-muted" style={{ fontSize: "0.7rem" }}>Term + Other Paid</div>
                      <div className="fw-semibold" style={{ fontSize: "0.82rem", color: TEXT_DARK }}>
                        {formatCurrency(
                          Number(center.term_paid_amount || 0) + Number(center.otherfees_paid_amount || 0)
                        )}
                      </div>
                    </div>

                    <div className="border-start ps-3">
                      <div className="text-muted" style={{ fontSize: "0.7rem" }}>Late Fee Paid</div>
                      <div className="fw-semibold" style={{ fontSize: "0.82rem", color: BRAND_PRIMARY }}>
                        {formatCurrency(
                          Number(center.term_latefee_paid_amount || 0) +
                          Number(center.otherfees_latefee_paid_amount || 0)
                        )}
                      </div>
                    </div>

                    <div className="border-start ps-3">
                      <div className="text-muted" style={{ fontSize: "0.7rem" }}>Overall Paid</div>
                      <div className="fw-bold" style={{ fontSize: "0.88rem", color: COLOR_SUCCESS }}>
                        {formatCurrency(collected)}
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Collection Progress & Action Button */}
                <Col xs={12} md={3} className="text-md-end">
                  <div className="d-flex flex-column align-items-md-end">
                    <div className="fw-bold text-success" style={{ fontSize: "1.05rem" }}>
                      {formatCurrency(collected)}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-1 w-100 justify-content-md-end">
                      <Progress
                        value={collectionPercent}
                        color="success"
                        style={{ width: "90px", height: "5px", borderRadius: "3px" }}
                      />
                      <span className="fw-semibold text-muted" style={{ fontSize: "0.75rem" }}>
                        {collectionPercent}%
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Collapsible Detailed View */}
            <Collapse isOpen={isExpanded}>
              <CardBody className="bg-light py-3 px-3">
                {/* Term & Other Fee Breakdown Cards */}
                <Row className="g-3 mb-3">
                  <Col xs={12} lg={6}>
                    <FeeBreakdownCard
                      title="Term Fees Breakdown"
                      paid={center.term_paid_amount}
                      total={
                        Number(center.term_total_amount) +
                        Number(center.term_skipped_amount) +
                        Number(center.term_expected_latefee_amount)
                      }
                      historicalPaid={center.term_historical_paid_amount}
                      refund={center.term_refund_amount}
                      discount={center.term_expected_discount}
                      skipped={center.term_skipped_amount}
                      lateTotal={center.term_expected_latefee_amount}
                      latePaid={center.term_latefee_paid_amount}
                      lateRefund={center.term_late_refund_amount}
                      balance={center.term_balance_amount}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <FeeBreakdownCard
                      title="Other Fees Breakdown"
                      paid={center.otherfees_paid_amount}
                      total={center.otherfees_total_amount}
                      refund={center.otherfees_refund_amount}
                      lateTotal={center.otherfees_expected_latefee_amount}
                      latePaid={center.otherfees_latefee_paid_amount}
                      lateRefund={center.otherfees_late_refund_amount}
                      discount={0}
                      balance={center.otherfees_balance_amount}
                    />
                  </Col>
                </Row>

                {/* Observation & Registration Badges */}
                <Row className="g-3 mb-3">
                  <Col xs={12} sm={6}>
                    <InfoCard
                      title="Observation Fees Paid"
                      amount={center.observation_paid_amount}
                      color="#6f42c1"
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <InfoCard
                      title="Registration Fees Paid"
                      amount={center.registeration_paid_amount}
                      color="#20c997"
                    />
                  </Col>
                </Row>

                {/* Center Payment Channels Table */}
                <Card className="border shadow-none mb-0">
                  <CardBody className="py-2 px-3">
                    <div className="fw-semibold mb-2" style={{ fontSize: "0.8rem", color: TEXT_DARK }}>
                      Center Payment Channels Breakdown
                    </div>
                    <div className="table-responsive">
                      <Table bordered size="sm" striped className="align-middle mb-0" style={{ fontSize: "0.82rem" }}>
                        <thead className="table-light">
                          <tr>
                            <th className="px-3 py-2">Payment Channel</th>
                            <th className="px-3 py-2 text-end">Amount Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(center.payment_channels || {}).map(([channel, amount], i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5">{channel}</td>
                              <td className="px-3 py-1.5 text-end font-monospace">{formatCurrency(amount)}</td>
                            </tr>
                          ))}
                          <tr className="fw-bold" style={{ backgroundColor: "#fef6ef" }}>
                            <td className="px-3 py-2" style={{ color: BRAND_PRIMARY }}>Total Channels Amount</td>
                            <td className="px-3 py-2 text-end font-monospace" style={{ color: BRAND_PRIMARY }}>
                              {formatCurrency(totalChannel)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Collapse>

            {/* Expand / Collapse Action Footer */}
            <div className="text-center bg-white border-top py-1.5">
              <Button
                color="link"
                className="text-decoration-none fw-semibold p-1"
                style={{ fontSize: "0.8rem", color: BRAND_PRIMARY }}
                onClick={() => setExpandedCenter(isExpanded ? null : center.center_id)}
              >
                {isExpanded ? (
                  <>Hide Details <FaChevronUp className="ms-1" size={10} /></>
                ) : (
                  <>View Details <FaChevronDown className="ms-1" size={10} /></>
                )}
              </Button>
            </div>
          </Card>
        );
      })}
    </Container>
  );
};

// ─── Component: MetricCard ───────────────────────────────────────────────────
const MetricCard = ({
  title,
  value,
  valueColor = TEXT_DARK,
  icon,
  topAccentColor = BRAND_PRIMARY,
  subText,
  tooltipTitle,
  tooltipRows,
  totalLabel,
  totalValue,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      className="border shadow-none h-100"
      style={{ borderTop: `3px solid ${topAccentColor}`, borderRadius: "4px" }}
    >
      <CardBody className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center">
            {icon}
            <span className="text-uppercase fw-semibold text-muted" style={{ fontSize: "0.75rem", letterSpacing: "0.4px" }}>
              {title}
            </span>
          </div>
        </div>

        <div className="fw-bold" style={{ fontSize: "1.5rem", color: valueColor, lineHeight: 1.2 }}>
          {value}
        </div>

        {subText && (
          <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
            {subText}
          </div>
        )}

        {/* ── Collapse / Expand Breakdown Bar ── */}
        {tooltipRows && tooltipRows.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="btn btn-link p-0 mt-2 text-decoration-none fw-semibold d-inline-flex align-items-center"
              style={{ fontSize: "0.75rem", color: BRAND_PRIMARY }}
              aria-expanded={expanded}
            >
              {expanded ? (
                <>Hide {tooltipTitle || "Breakdown"} <FaChevronUp className="ms-1" size={10} /></>
              ) : (
                <>View {tooltipTitle || "Breakdown"} <FaChevronDown className="ms-1" size={10} /></>
              )}
            </button>
            <Collapse isOpen={expanded}>
              <div className="mt-2 pt-2 border-top">
                <table className="w-100 mb-0" style={{ fontSize: "0.78rem" }}>
                  <tbody>
                    {tooltipRows.map((row, idx) => (
                      <tr key={idx}>
                        <td className="py-1" style={{ color: TEXT_MUTED }}>{row.label}</td>
                        <td className="text-end fw-semibold py-1" style={{ color: row.color || TEXT_DARK }}>
                          {row.value}
                        </td>
                      </tr>
                    ))}
                    <tr className="fw-bold border-top">
                      <td className="py-1" style={{ color: TEXT_DARK }}>{totalLabel}</td>
                      <td className="text-end py-1" style={{ color: TEXT_DARK }}>
                        {totalValue}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Collapse>
          </>
        )}
      </CardBody>
    </Card>
  );
};

// ─── Component: FeeBreakdownCard ─────────────────────────────────────────────
const FeeBreakdownCard = ({
  title,
  total,
  paid,
  historicalPaid = 0,
  refund = 0,
  discount = 0,
  skipped = 0,
  lateTotal = 0,
  latePaid = 0,
  lateRefund = 0,
  balance,
}) => {
  const displayLatePaid = Math.max(0, Number(latePaid));
  const lateUnpaid = Math.max(0, Number(lateTotal) - displayLatePaid - Number(lateRefund));
  const computedBalance = Math.max(
    0,
    Number(total) - Number(paid) - Number(refund) - displayLatePaid - Number(discount)
  );
  const displayBalance = balance !== undefined ? Math.max(0, Number(balance)) : computedBalance;
  const pctPaid = calculatePercentage(paid, total);

  return (
    <Card className="border shadow-none h-100 mb-0">
      <div className="px-3 py-2 bg-light border-bottom d-flex justify-content-between align-items-center">
        <span className="fw-bold" style={{ fontSize: "0.82rem", color: TEXT_DARK }}>
          {title}
        </span>
        <span
          className="badge fw-semibold"
          style={{ backgroundColor: "#eefaf5", color: "#166534", fontSize: "0.72rem" }}
        >
          {pctPaid}% Paid
        </span>
      </div>

      <CardBody className="p-3">
        <Progress
          value={pctPaid}
          color="success"
          className="mb-3"
          style={{ height: "5px", borderRadius: "3px" }}
        />

        <Table size="sm" borderless responsive className="mb-0" style={{ fontSize: "0.82rem" }}>
          <tbody>
            <tr className="border-bottom">
              <td className="text-muted py-1">Total Amount Billed</td>
              <td className="text-end fw-semibold py-1">{formatCurrency(total)}</td>
            </tr>
            <tr className="border-bottom">
              <td className="text-muted py-1">Amount Paid</td>
              <td className="text-end fw-semibold text-success py-1">{formatCurrency(paid)}</td>
            </tr>

            {Number(historicalPaid) > 0 && (
              <tr className="border-bottom">
                <td className="text-muted py-1">Historical Paid</td>
                <td className="text-end text-info py-1">{formatCurrency(historicalPaid)}</td>
              </tr>
            )}

            {Number(refund) > 0 && (
              <tr className="border-bottom">
                <td className="text-muted py-1">Refund Amount</td>
                <td className="text-end text-danger py-1">{formatCurrency(refund)}</td>
              </tr>
            )}

            {Number(discount) > 0 && (
              <tr className="border-bottom">
                <td className="text-muted py-1">Discount Amount</td>
                <td className="text-end py-1" style={{ color: BRAND_PRIMARY }}>
                  {formatCurrency(discount)}
                </td>
              </tr>
            )}

            {Number(skipped) > 0 && (
              <tr className="border-bottom">
                <td className="text-muted py-1">Skipped Amount</td>
                <td className="text-end text-muted py-1">{formatCurrency(skipped)}</td>
              </tr>
            )}

            {Number(lateTotal) > 0 && (
              <tr className="border-bottom">
                <td colSpan={2} className="py-1">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Total Late Fee</span>
                    <span className="fw-semibold" style={{ color: BRAND_PRIMARY }}>
                      {formatCurrency(lateTotal)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between ps-3 mt-0.5 text-muted" style={{ fontSize: "0.75rem" }}>
                    <span>- Paid</span>
                    <span className="text-success">{formatCurrency(displayLatePaid)}</span>
                  </div>
                  <div className="d-flex justify-content-between ps-3 mt-0.5 text-muted" style={{ fontSize: "0.75rem" }}>
                    <span>- Unpaid</span>
                    <span className="text-danger">{formatCurrency(lateUnpaid)}</span>
                  </div>
                  {Number(lateRefund) > 0 && (
                    <div className="d-flex justify-content-between ps-3 mt-0.5 text-muted" style={{ fontSize: "0.75rem" }}>
                      <span>- Refunded</span>
                      <span className="text-info">-{formatCurrency(lateRefund)}</span>
                    </div>
                  )}
                </td>
              </tr>
            )}

            <tr className="fw-bold" style={{ backgroundColor: displayBalance > 0 ? "#fdf2f2" : "#f0fdf4" }}>
              <td className="py-2 px-2" style={{ color: TEXT_DARK }}>Remaining Balance</td>
              <td className="text-end py-2 px-2" style={{ color: displayBalance > 0 ? COLOR_DANGER : COLOR_SUCCESS }}>
                {formatCurrency(displayBalance)}
              </td>
            </tr>
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

// ─── Component: InfoCard ─────────────────────────────────────────────────────
const InfoCard = ({ title, amount, color }) => (
  <Card className="border shadow-none mb-0">
    <CardBody className="p-3 d-flex justify-content-between align-items-center">
      <span className="fw-bold" style={{ fontSize: "0.82rem", color }}>
        {title}
      </span>
      <span className="fw-bold text-success" style={{ fontSize: "0.88rem" }}>
        {formatCurrency(amount)}
      </span>
    </CardBody>
  </Card>
);

export default AcademicFeesDashboard;
